import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Field } from "./Field";
import { FormSuccess } from "./FormSuccess";
import { enquiryMessage, whatsappLink } from "@/lib/whatsapp";
import { makeReference, submitNetlifyForm } from "@/lib/netlifyForms";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MIN_FILL_MS = 2500;

const validate = (fields, values) => {
  const errors = {};
  fields.forEach((f) => {
    const v = values[f.name];
    if (f.required && (f.type === "checkbox" ? v !== true : !String(v || "").trim())) {
      errors[f.name] = f.type === "checkbox" ? "Please confirm to continue." : "This field is required.";
    }
  });
  if (values.email && !EMAIL_RE.test(values.email)) errors.email = "Please enter a valid email address.";
  return errors;
};

/**
 * Generic enquiry form driven by a config from content/forms.js.
 * Delivery: FastAPI backend when REACT_APP_BACKEND_URL is set, otherwise Netlify Forms (emailed by Netlify).
 * Spam protection: hidden honeypot field + minimum fill time (checked client and server side) + server rate limit.
 */
export const EnquiryForm = ({ config, idPrefix, initialValues = {} }) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [reference, setReference] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const startedAt = useRef(Date.now());

  const waLink = useMemo(() => whatsappLink(enquiryMessage(config.type, config.fields, values, reference)), [config, values, reference]);

  const onChange = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const found = validate(config.fields, values);
    setErrors(found);
    if (Object.keys(found).length) {
      document.getElementById(`${idPrefix}-${Object.keys(found)[0]}`)?.focus();
      return;
    }
    const elapsed = Date.now() - startedAt.current;
    if (honeypot) return;
    if (elapsed < MIN_FILL_MS) {
      toast.error("That was quick. Please review your details and submit again.");
      return;
    }
    if (!BACKEND) {
      const ref = makeReference();
      setStatus("sending");
      try {
        await submitNetlifyForm(config.type, config.fields, values, ref);
        setReference(ref);
        setStatus("done");
        toast.success("Enquiry received. Continue on WhatsApp to reach SPC directly.");
      } catch {
        setStatus("offline");
      }
      return;
    }
    setStatus("sending");
    try {
      const res = await axios.post(`${API}/enquiries`, { form_type: config.type, data: values, website: honeypot, elapsed_ms: elapsed });
      setReference(res.data?.reference ?? "");
      setStatus("done");
      toast.success("Enquiry received. Continue on WhatsApp to reach SPC directly.");
    } catch (err) {
      const code = err?.response?.status;
      if (code === 429 || code === 422 || code === 400) {
        setStatus("idle");
        toast.error(err.response?.data?.detail?.toString?.() || "Please check your details and try again.");
      } else {
        setStatus("offline");
      }
    }
  };

  const reset = () => {
    setValues(initialValues);
    setReference("");
    setStatus("idle");
    startedAt.current = Date.now();
  };

  if (status === "done" || status === "offline") {
    return <FormSuccess reference={reference} waLink={waLink} mode={status} onReset={reset} idPrefix={idPrefix} />;
  }

  return (
    <form onSubmit={onSubmit} noValidate className="grid gap-x-5 gap-y-6 sm:grid-cols-2" data-testid={`${idPrefix}-form`}>
      {config.fields.map((f) => (
        <Field key={f.name} field={f} value={values[f.name]} error={errors[f.name]} onChange={onChange} idPrefix={idPrefix} />
      ))}
      <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor={`${idPrefix}-website`}>Website</label>
        <input id={`${idPrefix}-website`} type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} data-testid={`${idPrefix}-honeypot`} />
      </div>
      <div className="flex flex-col gap-4 pt-2 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[13px] text-muted">Fields marked * are required. We reply by email or WhatsApp.</p>
        <button
          type="submit"
          disabled={status === "sending"}
          className="inline-flex items-center justify-center gap-2 rounded-none bg-ink px-7 py-3.5 text-[15px] font-semibold text-paper transition-[background-color,color,transform] duration-300 hover:bg-accent hover:text-accent-ink active:scale-[0.98] disabled:opacity-60"
          data-testid={`${idPrefix}-submit-button`}
        >
          {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}
          {status === "sending" ? "Sending…" : config.submitLabel}
        </button>
      </div>
    </form>
  );
};
